import { getVocabularyStats, computeGrammarProgressRecentCEFR, readProgress, computeCEFR } from "../../utils/progress.js";
import { SpecimenCollection } from "../../components/SpecimenCollection.js";
import { ComingSoon } from "../../components/ComingSoon.js";
import { useEffect, useState } from "react";
import { useData } from "vike-react/useData";
import { usePageContext } from "vike-react/usePageContext";
import type { Data } from "./+data";
import { getScenes } from "../../components/Scenes.js";
import { langCodeToName } from "../../utils/appTheme.js";
import type { Language } from "../../components/Scenes.js";

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg: 'var(--bg)', surf: 'var(--surf)', surf2: 'var(--surf2)',
  border: 'var(--border)', text: 'var(--text)', muted: 'var(--muted)', dim: 'var(--dim)',
};
const AF = {
  h: 'var(--font-h, "Space Grotesk", system-ui, sans-serif)',
  b: "'DM Sans', system-ui, sans-serif",
  m: "'DM Mono', 'Courier New', monospace",
};

// ── Skeleton bar ─────────────────────────────────────────────────────────────
function Skel({ w, h = 13 }: { w: number | string; h?: number }) {
  return (
    <span style={{
      display: 'inline-block', width: w, height: h, borderRadius: 4,
      background: 'var(--surf2)', animation: 'c8-skel 1.4s ease-in-out infinite',
      verticalAlign: 'middle',
    }}/>
  );
}

// ── City / locale / weather config ────────────────────────────────────────────
const CITY_CFG: Record<string, { city: string; lat: number; lon: number; tz: string; locale: string }> = {
  es: { city: 'Madrid',    lat: 40.4168,  lon: -3.7038,  tz: 'Europe/Madrid',     locale: 'es-ES' },
  pt: { city: 'São Paulo', lat: -23.5505, lon: -46.6333, tz: 'America/Sao_Paulo', locale: 'pt-BR' },
  fi: { city: 'Helsinki',  lat: 60.1699,  lon: 24.9384,  tz: 'Europe/Helsinki',   locale: 'fi-FI' },
};

const WMO: Record<string, Record<number, string>> = {
  es: { 0:'Despejado',1:'Mayormente despejado',2:'Parcialmente nublado',3:'Nublado',45:'Niebla',48:'Niebla',51:'Llovizna',53:'Llovizna',55:'Llovizna',61:'Lluvia ligera',63:'Lluvia',65:'Lluvia intensa',71:'Nevada',73:'Nevada',75:'Nevada',80:'Chubascos',81:'Chubascos',82:'Chubascos',95:'Tormenta',96:'Tormenta' },
  pt: { 0:'Céu limpo',1:'Principalmente limpo',2:'Parcialmente nublado',3:'Nublado',45:'Névoa',48:'Névoa',51:'Garoa',53:'Garoa',55:'Garoa',61:'Chuva fraca',63:'Chuva',65:'Chuva forte',71:'Nevada',73:'Nevada',75:'Nevada',80:'Pancadas',81:'Pancadas',82:'Pancadas',95:'Trovoada',96:'Trovoada' },
  fi: { 0:'Selkeää',1:'Selkeää',2:'Puolipilvistä',3:'Pilvistä',45:'Sumua',51:'Tihkua',61:'Sadetta',63:'Sadetta',65:'Sadetta',71:'Lumisadetta',80:'Sadekuuroja',95:'Ukkosmyrsky' },
};

function wmoLabel(langCode: string, code: number): string {
  const t = WMO[langCode] ?? WMO.es;
  return t[code] ?? t[Math.floor(code / 10) * 10] ?? '';
}

function wmoEmoji(code: number): string {
  if (code === 0) return '☀️';
  if (code <= 1) return '🌤️';
  if (code <= 3) return '☁️';
  if (code <= 48) return '🌫️';
  if (code <= 55) return '🌦️';
  if (code <= 65) return '🌧️';
  if (code <= 75) return '🌨️';
  if (code <= 82) return '🌧️';
  return '⛈️';
}

function computeStreak(): number {
  if (typeof window === 'undefined') return 0;
  const store = readProgress();
  const days = new Set<string>();
  for (const s of Object.values(store.byConjugation)) {
    for (const h of (s.history ?? [])) {
      const d = new Date(h.timestamp);
      days.add(`${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`);
    }
  }
  let streak = 0;
  const today = new Date();
  for (let i = 0; i < 365; i++) {
    const d = new Date(today); d.setDate(today.getDate() - i);
    const key = `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
    if (days.has(key)) streak++;
    else if (i > 0) break;
  }
  return streak;
}

function useLocationData(langCode: string) {
  const [weather, setWeather] = useState<{ temp: number; label: string; emoji: string } | null>(null);
  const [localTime, setLocalTime] = useState('');

  useEffect(() => {
    const cfg = CITY_CFG[langCode] ?? CITY_CFG.es;
    const tz = encodeURIComponent(cfg.tz);
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${cfg.lat}&longitude=${cfg.lon}&current=temperature_2m,weathercode&timezone=${tz}`)
      .then(r => r.json())
      .then(d => {
        const code: number = d.current?.weathercode ?? 0;
        const temp: number = Math.round(d.current?.temperature_2m ?? 0);
        setWeather({ temp, label: wmoLabel(langCode, code), emoji: wmoEmoji(code) });
      })
      .catch(() => {});
  }, [langCode]);

  useEffect(() => {
    const cfg = CITY_CFG[langCode] ?? CITY_CFG.es;
    const updateTime = () => {
      try {
        setLocalTime(new Intl.DateTimeFormat('en', { hour: '2-digit', minute: '2-digit', hour12: false, timeZone: cfg.tz }).format(new Date()));
      } catch { setLocalTime(''); }
    };
    updateTime();
    const timer = setInterval(updateTime, 30_000);
    return () => clearInterval(timer);
  }, [langCode]);

  return { weather, localTime };
}

// ── Language greetings ────────────────────────────────────────────────────────
const LANG_GREETING: Record<Language, { greeting: string; city: string }> = {
  Spanish:    { greeting: '¡Bienvenido de nuevo.', city: 'Madrid' },
  Portuguese: { greeting: 'Bom dia.',              city: 'São Paulo' },
  Finnish:    { greeting: 'Hyvää huomenta.',       city: 'Helsinki' },
};

// ── Main dashboard ────────────────────────────────────────────────────────────
export default function Page() {
  const data = useData<Data>();
  const pageContext = usePageContext();
  const langCode = pageContext.routeParams?.language || 'es';
  const comingSoon = pageContext.routeParams?.comingSoon === 'true';

  const languageNameMap: Record<string, string> = {
    es: 'spanish', pt: 'portuguese', fr: 'french',
    it: 'italian', ca: 'catalan', ro: 'romanian', fi: 'finnish',
  };
  const languageName = languageNameMap[langCode] || 'spanish';
  const prefix = `/learn-${languageName}`;
  const language = langCodeToName(langCode);
  const lc = LANG_GREETING[language] ?? LANG_GREETING.Spanish;

  const [vocabStats, setVocabStats] = useState({ wellKnownWords: 0, totalWords: 0 });
  const [grammarProgress, setGrammarProgress] = useState<{ progressPct: number }>(
    data.initialGrammarProgress ?? { progressPct: 0 }
  );
  const [isMobile, setIsMobile] = useState(false);
  const [streak, setStreak] = useState(0);
  const [cefrLabel, setCefrLabel] = useState<string>('—');
  const { weather, localTime } = useLocationData(langCode);

  useEffect(() => {
    setIsMobile(window.innerWidth < 768);
    const h = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);

  useEffect(() => {
    setVocabStats(getVocabularyStats());
    setStreak(computeStreak());
    const grade = computeCEFR(langCode);
    setCefrLabel(grade.label);
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const gp = await computeGrammarProgressRecentCEFR(langCode);
        setGrammarProgress(gp);
      } catch {}
    })();
  }, [langCode]);

  if (comingSoon) return <ComingSoon language={langCode}/>;

  const hp = isMobile ? '16px' : '48px';
  const mastered = vocabStats.wellKnownWords;
  const scenes = getScenes(language);
  const [Scene0, Scene1, Scene2] = scenes;

  return (
    <div style={{ flex: 1, overflowY: 'auto', background: C.bg }}>

      {/* ── Atmospheric header ── */}
      <div style={{ position: 'relative', overflow: 'hidden', minHeight: isMobile ? 130 : 170 }}>
        <div style={{ position: 'absolute', inset: 0, display: 'flex', opacity: 'var(--scene-opacity)' as any, pointerEvents: 'none' }}>
          <div style={{ flex: 1, overflow: 'hidden' }}><Scene0/></div>
          <div style={{ flex: 1, overflow: 'hidden' }}><Scene1/></div>
          <div style={{ flex: 1, overflow: 'hidden' }}><Scene2/></div>
        </div>
        <div style={{ position: 'absolute', inset: 0, background: 'var(--header-grad)', pointerEvents: 'none' }}/>
        <div style={{ position: 'relative', zIndex: 1, padding: isMobile ? '24px 16px 20px' : '30px 48px 24px' }}>
          <h1 style={{ fontFamily: AF.h, fontSize: isMobile ? 22 : 28, fontWeight: 700, color: C.text, margin: 0, letterSpacing: '-0.8px', textShadow: '0 1px 8px rgba(0,0,0,0.4)' }}>
            {lc.greeting}
          </h1>
          <p style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, margin: '4px 0 0', display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <span>{lc.city}</span>
            <span style={{ opacity: 0.4 }}>·</span>
            {localTime ? <span>{localTime}</span> : <Skel w={38}/>}
            {weather && (
              <>
                <span style={{ opacity: 0.4 }}>·</span>
                <span>{weather.emoji} {weather.temp}°C</span>
                <span style={{ opacity: 0.75 }}>{weather.label}</span>
              </>
            )}
          </p>
        </div>
      </div>

      {/* ── Conjugarden — primary action ── */}
      <div style={{ padding: `20px ${hp} 0` }}>
        <a href={`${prefix}/garden`} style={{
          display: 'block', textDecoration: 'none', borderRadius: 16, overflow: 'hidden',
          border: '1px solid var(--accent-border)',
          background: 'linear-gradient(115deg, var(--accent-bg), var(--surf) 60%)',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 14 : 22, padding: isMobile ? '20px 18px' : '26px 30px', flexWrap: 'wrap' }}>
            <div style={{ fontSize: isMobile ? 40 : 52, lineHeight: 1, flexShrink: 0 }}>🌿</div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontFamily: AF.m, fontSize: 10, color: 'var(--accent)', letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 6 }}>Start learning</div>
              <div style={{ fontFamily: AF.h, fontSize: isMobile ? 24 : 30, fontWeight: 700, color: C.text, letterSpacing: '-0.7px', marginBottom: 5 }}>
                Enter the Conjugarden
              </div>
              <div style={{ fontFamily: AF.b, fontSize: 13, color: C.muted, lineHeight: 1.5 }}>
                Run expeditions, grow your garden, discover wild verbs.
              </div>
            </div>
            <span style={{
              flexShrink: 0, fontFamily: AF.b, fontSize: 15, fontWeight: 700,
              background: 'var(--accent)', color: C.bg,
              padding: isMobile ? '12px 20px' : '14px 28px', borderRadius: 10, whiteSpace: 'nowrap',
            }}>Begin →</span>
          </div>
        </a>
      </div>

      {/* ── Training shortcuts ── */}
      <div style={{ padding: `10px ${hp} 0`, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
        <span style={{ fontFamily: AF.b, fontSize: 13, color: C.dim }}>or</span>
        <a href={`${prefix}/grammar`} style={{
          fontFamily: AF.b, fontSize: 13, color: 'var(--accent)', textDecoration: 'none',
          padding: '6px 14px', border: '1px solid var(--accent-border)', borderRadius: 7,
          background: 'var(--accent-bg)',
        }}>Continue training →</a>
        <a href={`${prefix}/vocabulary`} style={{
          fontFamily: AF.b, fontSize: 13, color: C.muted, textDecoration: 'none',
          padding: '6px 14px', border: `1px solid ${C.border}`, borderRadius: 7,
          background: C.surf,
        }}>Vocabulary →</a>
      </div>

      {/* ── Progress snapshot ── */}
      <div style={{ padding: `16px ${hp} 28px` }}>
        <div style={{
          background: C.surf, border: `1px solid ${C.border}`, borderRadius: 14,
          padding: isMobile ? '18px 16px' : '22px 28px',
          display: 'flex', alignItems: 'center', gap: isMobile ? 16 : 32,
          flexWrap: isMobile ? 'wrap' : 'nowrap',
        }}>

          {/* CEFR level badge */}
          <div style={{ flexShrink: 0, textAlign: 'center', minWidth: isMobile ? 64 : 72 }}>
            <div style={{ fontFamily: AF.h, fontSize: 44, fontWeight: 800, color: 'var(--accent)', letterSpacing: '-2px', lineHeight: 1 }}>
              {cefrLabel}
            </div>
            <div style={{ fontFamily: AF.b, fontSize: 11, color: C.muted, marginTop: 3 }}>CEFR level</div>
          </div>

          {!isMobile && <div style={{ width: 1, alignSelf: 'stretch', background: C.border, flexShrink: 0 }}/>}

          {/* Grammar + Vocab bars */}
          <div style={{ flex: 1, minWidth: 140, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                <span style={{ fontFamily: AF.b, fontSize: 12, color: C.muted }}>Grammar</span>
                <span style={{ fontFamily: AF.m, fontSize: 12, color: C.text }}>{Math.round(grammarProgress.progressPct)}%</span>
              </div>
              <div style={{ height: 7, background: C.surf2, borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${Math.max(2, grammarProgress.progressPct)}%`, height: '100%', background: 'var(--accent)', borderRadius: 4, transition: 'width 0.6s' }}/>
              </div>
            </div>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 5 }}>
                <span style={{ fontFamily: AF.b, fontSize: 12, color: C.muted }}>Vocabulary</span>
                <span style={{ fontFamily: AF.m, fontSize: 12, color: C.text }}>
                  {mastered} <span style={{ color: C.dim }}>/ 2400</span>
                </span>
              </div>
              <div style={{ height: 7, background: C.surf2, borderRadius: 4, overflow: 'hidden' }}>
                <div style={{ width: `${Math.max(mastered > 0 ? 1 : 0, Math.min(100, (mastered / 2400) * 100))}%`, height: '100%', background: 'var(--success, #22c55e)', borderRadius: 4, transition: 'width 0.6s' }}/>
              </div>
            </div>
          </div>

          {!isMobile && <div style={{ width: 1, alignSelf: 'stretch', background: C.border, flexShrink: 0 }}/>}

          {/* Streak */}
          <div style={{ flexShrink: 0, textAlign: 'center', minWidth: isMobile ? 56 : 64 }}>
            <div style={{ fontFamily: AF.h, fontSize: 40, fontWeight: 800, letterSpacing: '-1.5px', lineHeight: 1, color: streak > 0 ? 'var(--accent)' : C.dim }}>
              {streak > 0 ? streak : '—'}
            </div>
            <div style={{ fontFamily: AF.b, fontSize: 11, color: C.muted, marginTop: 3 }}>
              {streak > 0 ? 'day streak 🔥' : 'no streak yet'}
            </div>
          </div>

        </div>
      </div>

      {/* ── Field Specimen Collection ── */}
      <div style={{ padding: `0 ${hp} 40px` }}>
        <SpecimenCollection prefix={prefix} />
      </div>

    </div>
  );
}
